import type { MaybeRefOrGetter } from 'vue';
import type { UsePersistedStateOptions } from '#layers/base/public';
import type { UseBlockStorageKeyOptions } from './use-block-storage-key';
import { usePersistedState } from '#layers/base/public';
import { useBlockInEditorContext } from '#layers/block-engine/utils/block/api/context';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useBlockStorageKey } from './use-block-storage-key';

export interface UsePersistedBlockStateOptions extends Omit<UsePersistedStateOptions, 'blockId' | 'ownerId' | 'store' | 'slug'>, UseBlockStorageKeyOptions {}

/**
 * Manages a reactive state with persistence, scoped by block context, user, and collection details.
 *
 * @param {MaybeRefOrGetter<string>} key - The base key used for identifying the persisted state.
 * @param {MaybeRefOrGetter<T>} defaults - The default value to use if no existing persisted value is found.
 * @param {UsePersistedBlockStateOptions} options - Storage availability and optional user, collection, and persistence settings.
 * @return {ReturnType<typeof usePersistedState>} A reactive state object that manages persisted values according to the computed storage key based on context.
 */
export function usePersistedBlockState<T>(key: MaybeRefOrGetter<string>, defaults: MaybeRefOrGetter<T>, options: UsePersistedBlockStateOptions) {
	const { writeDefaults } = options;
	const { blockInEditor } = useBlockInEditorContext();
	const { store, slug } = useWorkspaceRuntimeHandle();

	const { storageKey, rootBlockId, collectionId, ownerId } = useBlockStorageKey(key, {
		collectionId: options.collectionId,
		userId: options.userId,
	});

	return usePersistedState(storageKey, defaults, {
		...options,
		store,
		slug,
		blockId: rootBlockId,
		collectionId,
		ownerId,
		writeDefaults: writeDefaults ?? !blockInEditor,
	});
}
